import { Checkbox as HeadlessCheckbox } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { Fragment } from 'react/jsx-runtime';
import { FormFeedback, FormField } from '~/components/forms';
interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  className?: string;
  description?: string;
  label?: string;
  cyTag?: string;
  required?: boolean;
  errorCyTag?: string;
  reserveFeedbackSpace?: boolean;
  options: { label: string; value: string }[];
}
export const EnumCheckbox = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    className,
    description,
    label,
    cyTag,
    required,
    errorCyTag,
    reserveFeedbackSpace,
    options,
  } = props;

  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error } }) => {
        const { value, onChange } = field;
        const toggle = (option: { value: string; label: string }) => {
          onChange(option);
        };

        const errorId = `${name}-error`;
        const helperId = `${name}-helper`;

        return (
          <FormField
            id={name}
            className={className}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={errorId}
                errorCyTag={errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined)}
                description={description}
                helperId={helperId}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <fieldset
              className="m-0 min-w-0 border-0 p-0"
              data-json-form-field={name}
              tabIndex={-1}
              aria-labelledby={label ? `${name}-label` : undefined}
              aria-invalid={!!error || undefined}
              aria-required={required || undefined}
              aria-describedby={error?.message ? errorId : description ? helperId : undefined}
              aria-errormessage={error?.message ? errorId : undefined}
            >
              {label && (
                <legend
                  id={`${name}-label`}
                  className="body-text-sb text-left text-content-primary"
                >
                  {label}
                  {required && (
                    <>
                      <span className="pl-px text-content-primary" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only">(required)</span>
                    </>
                  )}
                </legend>
              )}
              <div className={`grid grid-cols-2 items-center ${label ? 'mt-2' : ''}`}>
                {options.map((option) => (
                  <HeadlessCheckbox
                    key={option.value}
                    checked={value?.value == option.value || false}
                    onChange={() => toggle(option)}
                    as={Fragment}
                    data-cy={`${name}-${option.label}${cyTag}`}
                  >
                    {({ checked }) => (
                      <label
                        className={`flex items-center gap-2 cursor-pointer rounded-md  p-2 ${
                          checked ? 'bg-primary-t-900 border-primary-50' : 'bg-white'
                        }`}
                      >
                        <span
                          className={`w-4 h-4 border rounded flex items-center justify-center ${
                            checked ? 'bg-primary-50 text-white' : 'bg-white'
                          }`}
                        >
                          {checked && <CheckIcon className="w-3 h-3 font-black" />}
                        </span>
                        {option.label}
                      </label>
                    )}
                  </HeadlessCheckbox>
                ))}
              </div>
            </fieldset>
          </FormField>
        );
      }}
    />
  );
};
